import React, { useState, useEffect, useCallback } from "react";
import PlacesAutocomplete, {
	geocodeByAddress,
	getLatLng,
} from "react-places-autocomplete";
import { FiMapPin } from "react-icons/fi";
import { FormLabel } from "../FormLabel";
import FormikFieldError from "../FormikFieldError";
import InfoTooltip from "newui/components/InfoTooltip";
import { getInputClasses, getInputWrapperClasses } from "../InputWithLabel/inputStyles";

export interface LocationValue {
	value: string;
	latitude?: number | null;
	longitude?: number | null;
}

interface LocationPickerProps {
	/** Label displayed above the input */
	label?: string;
	/** Current location string or structured value */
	value?: string | LocationValue | null;
	/** Callback when a location is selected or typed */
	onChange?: (location: string) => void;
	/** Callback with full geocoded result (address, lat, lng) */
	onSelect?: (result: LocationValue) => void;
	/** Placeholder text */
	placeholder?: string;
	/** Mark as required */
	required?: boolean;
	/** Disable the input */
	disabled?: boolean;
	/** Error message */
	errorText?: string;
	/** Error state flag */
	hasError?: boolean;
	/** Tooltip text */
	guideLine?: string;
	/** Restrict to specific country (ISO 3166-1 alpha-2), defaults to "au" */
	country?: string;
	/** Allow international search (overrides country restriction) */
	allowInternational?: boolean;
	/** Test ID */
	testId?: string;
	/** Additional wrapper classes */
	className?: string;
}

const resolveDisplayValue = (value?: string | LocationValue | null): string => {
	if (!value) {
		return "";
	}
	if (typeof value === "string") {
		return value;
	}
	return value.value ?? "";
};

const LocationPicker: React.FC<LocationPickerProps> = ({
	label,
	value,
	onChange,
	onSelect,
	placeholder = "Search address",
	required,
	disabled,
	errorText,
	hasError = false,
	guideLine,
	country = "au",
	allowInternational = false,
	testId,
	className = "",
}) => {
	const [inputValue, setInputValue] = useState(resolveDisplayValue(value));

	// Sync when the controlled value changes externally (e.g. modal open/reset)
	useEffect(() => {
		setInputValue(resolveDisplayValue(value));
	}, [value]);

	const searchOptions = {
		componentRestrictions: allowInternational ? {} : { country },
		language: "en",
	};

	const handleChange = useCallback(
		(address: string) => {
			setInputValue(address);
			onChange?.(address);
		},
		[onChange],
	);

	const handleSelect = useCallback(
		(address: string) => {
			setInputValue(address);

			geocodeByAddress(address)
				.then((results) => {
					const parts = address.split(",");
					const shortened = parts.length >= 2
						? parts.slice(0, 2).join(",")
						: address;

					return getLatLng(results[0]).then((latLng) => ({
						shortened,
						lat: latLng.lat,
						lng: latLng.lng,
					}));
				})
				.then(({ shortened, lat, lng }) => {
					setInputValue(shortened);
					onChange?.(shortened);
					onSelect?.({
						value: shortened,
						latitude: lat,
						longitude: lng,
					});
				})
				.catch((error) => {
					console.error("Geocoding error:", error);
					onChange?.(address);
				});
		},
		[onChange, onSelect],
	);

	const showError = hasError || !!errorText;

	return (
		<div className={getInputWrapperClasses(showError, false, className)}>
			{label && (
				<FormLabel label={label} required={required}>
					{guideLine && (
						<div className="inline-block">
							<InfoTooltip tooltipText={guideLine} />
						</div>
					)}
				</FormLabel>
			)}
			<PlacesAutocomplete
				value={inputValue}
				onChange={handleChange}
				onSelect={handleSelect}
				searchOptions={searchOptions}
				highlightFirstSuggestion
			>
				{({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
					<div className="relative">
						<div className="relative">
							<input
								{...getInputProps({
									placeholder,
									disabled,
									className: `${getInputClasses(showError, "h-14 max-h-14")} pr-10`,
									autoComplete: "off",
									"data-testid": testId,
								})}
							/>
							<FiMapPin
								className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
								size={16}
							/>
						</div>
						{(suggestions.length > 0 || loading) && (
							<div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
								{loading && (
									<div className="px-4 py-3 text-sm text-gray-400">
										Loading...
									</div>
								)}
								{suggestions.map((suggestion) => {
									const itemProps = getSuggestionItemProps(suggestion);
									return (
										<div
											{...itemProps}
											key={suggestion.placeId}
											className={`cursor-pointer px-4 py-2 text-sm ${
												suggestion.active
													? "bg-blue-50 text-gray-800"
													: "text-gray-700 hover:bg-blue-50"
											}`}
										>
											{suggestion.description}
										</div>
									);
								})}
							</div>
						)}
					</div>
				)}
			</PlacesAutocomplete>
			{showError && errorText && (
				<FormikFieldError text={errorText} />
			)}
		</div>
	);
};

export { LocationPicker };
export type { LocationPickerProps };
